import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import toast, { Toaster } from 'react-hot-toast';
import { motion } from 'framer-motion';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

const Login = () => {
  const [email, setEmail] = useState('test@gmail.com');
  const [password, setPassword] = useState('Test@123');
  const [localError, setLocalError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError(''); // Clear previous error
    setLoading(true);
    
    try {
      const result = await login(email, password);
      
      if (result.success) {
        toast.success('Welcome back!');
        navigate('/dashboard');
      } else {
        const errorMsg = result.error || 'Login failed';
        setLocalError(errorMsg);
        toast.error(errorMsg);
      }
    } catch (err) {
      const errorMsg = err.message || 'Login failed';
      setLocalError(errorMsg); // Set local error to show in form
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-white font-sans">
      <Toaster position="top-right" />
      
      {/* Left Panel: Graphic & Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-[#0f172a] overflow-hidden flex-col justify-between p-12 lg:p-20">
        
        {/* Cool Abstract Background */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 brightness-100 contrast-150 mix-blend-overlay"></div>
          <div className="absolute top-0 right-0 -translate-y-12 translate-x-1/3 w-[600px] h-[600px] bg-gradient-to-br from-[#dc343b] to-[#f43f5e] rounded-full blur-[120px] opacity-40"></div>
          <div className="absolute bottom-0 left-0 translate-y-1/3 -translate-x-1/3 w-[500px] h-[500px] bg-gradient-to-tr from-indigo-500 to-purple-500 rounded-full blur-[120px] opacity-30"></div>
        </div>

        {/* Branding */}
        <div className="relative z-10">
          <Link to="/" className="inline-flex items-center gap-3">
            <div className="flex items-center justify-center bg-[#dc343b] text-white p-2 rounded-xl shadow-lg">
              <svg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 24 24' fill='currentColor'>
                <path d='M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z'/>
              </svg>
            </div>
            <span className="text-2xl font-extrabold tracking-tight text-white uppercase">
              NETROOTX BILLING
            </span>
          </Link>
        </div>

        {/* Hero Text */}
        <div className="relative z-10 mt-auto mb-20">
          <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-[56px] leading-[1.1] mb-8">
            Manage your <br/> business seamlessly.
          </h1>
          <ul className="space-y-4">
            {['Track daily ledgers & cashflow', 'Automated payment reminders', '100% secure and backed up'].map((feature, i) => (
              <li key={i} className="flex items-center gap-3 text-lg font-medium text-gray-300">
                <CheckCircleIcon className="w-6 h-6 text-[#dc343b]" />
                {feature}
              </li>
            ))}
          </ul>
        </div>
        
        {/* Testimonial mini */}
        <div className="relative z-10 flex items-center gap-4 bg-white/10 backdrop-blur-md p-6 rounded-3xl border border-white/10">
          <img src="https://i.pravatar.cc/150?img=12" alt="Avatar" className="w-12 h-12 rounded-full border-2 border-white/20" />
          <div>
            <p className="text-sm font-semibold text-white">"It completely transformed how we do accounting."</p>
            <p className="text-xs font-medium text-gray-400 mt-1">Rahul Sharma, Enterprise Owner</p>
          </div>
        </div>

      </div>

      {/* Right Panel: Form */}
      <div className="flex flex-1 flex-col justify-center px-4 py-12 sm:px-6 lg:flex-none lg:w-1/2 lg:px-20 xl:px-32 relative">
        
        {/* Mobile Logo (hidden on desktop) */}
        <div className="absolute top-8 left-8 lg:hidden">
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="flex items-center justify-center bg-[#dc343b] text-white p-1.5 rounded-lg shadow-md">
              <svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='currentColor'>
                <path d='M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z'/>
              </svg>
            </div>
            <span className="text-xl font-extrabold tracking-tight text-[#0f172a] uppercase">Khatabook</span>
          </Link>
        </div>

        <motion.div 
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="mx-auto w-full max-w-sm lg:max-w-md"
        >
          <div className="mb-10">
            <h2 className="text-3xl font-extrabold tracking-tight text-gray-900 mb-3">
              Welcome back
            </h2>
            <p className="text-[15px] text-gray-500 font-medium">
              Please enter your details to sign in.
            </p>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            
            {/* Inline Error Message
            {localError && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }} 
                animate={{ opacity: 1, y: 0 }} 
                className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl flex items-center gap-3 text-[14px] font-medium"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {localError}
              </motion.div>
            )} */}

            <div>
              <label className="block text-[13px] font-bold tracking-wide text-gray-900 uppercase mb-2">Email Address</label>
              <div className="relative">
                <input
                  type="email"
                  required
                  className="block w-full rounded-xl border border-gray-200 px-5 py-3.5 text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-[#dc343b] focus:ring-1 focus:ring-[#dc343b] sm:text-[15px] bg-white transition-colors"
                  placeholder="john@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-[13px] font-bold tracking-wide text-gray-900 uppercase">Password</label>
                <a href="#" className="text-[13px] font-semibold text-[#dc343b] hover:text-[#b91c1c]">Forgot password?</a>
              </div>
              <div className="relative">
                <input
                  type="password"
                  required
                  className="block w-full rounded-xl border border-gray-200 px-5 py-3.5 text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-[#dc343b] focus:ring-1 focus:ring-[#dc343b] sm:text-[15px] bg-white transition-colors"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={loading}
                className="flex w-full justify-center items-center rounded-xl bg-[#0f172a] px-4 py-4 text-[15px] font-bold text-white shadow-lg shadow-slate-900/20 hover:bg-slate-800 hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : 'Sign In'}
              </button>
            </div>
            
            {/* Social Logins (Visual only for premium feel) */}
            <div className="mt-8">
              <div className="relative">
                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-white px-3 text-[13px] font-semibold text-gray-500 uppercase tracking-widest">or continue with</span>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-4">
                <button type="button" className="flex w-full items-center justify-center gap-3 rounded-xl bg-white px-3 py-3 text-[14px] font-semibold text-gray-900 ring-1 ring-inset ring-gray-200 hover:bg-gray-50 hover:ring-gray-300 transition-all">
                  <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  Google
                </button>
                <button type="button" className="flex w-full items-center justify-center gap-3 rounded-xl bg-white px-3 py-3 text-[14px] font-semibold text-gray-900 ring-1 ring-inset ring-gray-200 hover:bg-gray-50 hover:ring-gray-300 transition-all">
                  <svg className="h-5 w-5 fill-black" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.166 6.839 9.489.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.603-3.369-1.34-3.369-1.34-.454-1.156-1.11-1.462-1.11-1.462-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.831.092-.646.35-1.086.636-1.336-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0112 6.836c.85.004 1.705.114 2.504.336 1.909-1.294 2.747-1.025 2.747-1.025.546 1.379.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.138 20.161 22 16.416 22 12c0-5.523-4.477-10-10-10z" />
                  </svg>
                  GitHub
                </button>
              </div>
            </div>
            
          </form>

          <div className="mt-10 text-center text-[15px] text-gray-500">
            Don't have an account?{' '}
            <Link to="/register" className="font-semibold text-[#dc343b] hover:text-[#b91c1c]">
              Sign up for free
            </Link>
          </div>
        </motion.div>
      </div>

    </div>
  );
};

export default Login;
